<template>
  <div class="flex flex-col">
    <transition name="modal">
      <meal-set-form
        v-if="mealSetToUpdate"
        :mealSet="mealSetToUpdate"
        :dietCategories="dietCategories"
        :dietStages="dietStages"
        :dietTypes="dietTypes"
        @close="mealSetToUpdate = null"
        @updated="mealSetUpdated"
      />
    </transition>

    <transition name="modal">
      <alert-modal
        v-if="alertModalMessage"
        :message="alertModalMessage"
        @close="alertModalMessage = null"
      />
    </transition>

    <transition name="modal">
      <rejection-modal
        v-if="failedOrder"
        :loading="loadingOrder"
        message="El cliente deberá seleccionar nuevamente el método de envío para esta orden. ¿Podrías detallar cuál fue el problema?"
        @close="failedOrder = null; refundShipping = false"
        @confirm="failedToSend($event)"
      >
        <template #extra-fields>
          <label class="flex items-center mt-4 text-sm cursor-pointer">
            <input type="checkbox" v-model="refundShipping" class="mr-2" />
            Reembolsar cargo de envío al cliente
          </label>
        </template>
      </rejection-modal>
    </transition>

    <transition name="modal">
      <shipping-price-modal
        v-if="orderToSetShippingPrice"
        :order="orderToSetShippingPrice"
        :carriers="carriers"
        @close="orderToSetShippingPrice = null"
        @confirm="
          orderToSetShippingPrice.shippingPrice = $event;
          orderToSetShippingPrice = null;
        "
      />
    </transition>

    <transition name="modal">
      <tracking-info-modal
        v-if="orderToSendWithTracking"
        :order="orderToSendWithTracking"
        @close="orderToSendWithTracking = null"
        @updateTrackingInfo="sendOrderWithTrackingInfo"
      />
    </transition>

    <h2>Órdenes</h2>

    <custom-search
      class="mt-2 w-full sm:w-[500px]"
      placeholder="Buscar por No. de Orden, Cliente ó Mascota"
      v-model="searchText"
      @confirm="search"
      @clear="search"
    />

    <div class="flex flex-wrap mt-2 items-center space-x-2">
      <button
        :class="[
          'bg-white p-2 rounded font-medium mt-2 w-fit shadow-primary',
          { 'text-content-300': !enableStatusFilters },
          { 'text-primary-500': enableStatusFilters },
        ]"
        @click="changeStatusFilter"
      >
        Filtro de estatus
      </button>

      <button
        :class="[
          'bg-white p-2 rounded font-medium mt-2 w-fit shadow-primary',
          { 'text-content-300': petTypeFilter !== 'dog' },
          { 'text-primary-500': petTypeFilter === 'dog' },
        ]"
        @click="changePetTypeFilter('dog')"
      >
        Perros
      </button>

      <button
        :class="[
          'bg-white p-2 rounded font-medium mt-2 w-fit shadow-primary',
          { 'text-content-300': petTypeFilter !== 'cat' },
          { 'text-primary-500': petTypeFilter === 'cat' },
        ]"
        @click="changePetTypeFilter('cat')"
      >
        Gatos
      </button>

      <button
        :class="[
          'bg-white p-2 rounded font-medium mt-2 w-fit shadow-primary',
          { 'text-content-300': orderType !== 'desc' },
          { 'text-primary-500': orderType === 'desc' },
        ]"
        @click="changeOrderTypeFilter"
      >
        Últimas arriba
      </button>
    </div>

    <div class="flex flex-col lg:flex-row gap-4 lg:gap-6 mt-4 items-start">
      <status-side-nav v-if="enableStatusFilters" :options="statusNavOptions" />

      <div class="flex-1 min-w-0 w-full">

    <div v-if="!loading && orders && inventories" class="flex flex-col">
      <div v-if="status === 'pending'">
        <p class="mt-6" v-if="orders.length === 0">No hay órdenes pendientes</p>

        <pending-order
          v-for="order in orders"
          :key="order.id"
          :order="order"
          @updateMealSet="mealSetToUpdate = $event"
          class="card mt-6 w-full"
        >
          <primary-button
            v-if="canProcessOrderOrder(order) && hasPermission('edit_orders')"
            :loading="loadingOrder"
            @buttonClicked="
              confirmationModal = {
                action: 'processOrder',
                order: order,
                message:
                  '¿Estás seguro que deseas procesar e imprimir la orden?',
              }
            "
            class="mt-4 mx-0"
            >Procesar e Imprimir Orden
          </primary-button>

          <button
            v-if="canProcessOrderOrder(order) && hasPermission('edit_orders')"
            class="secondary-button mt-4 mx-0"
            @click.prevent="
              confirmationModal = {
                action: 'processOrderWithoutPrint',
                order: order,
                message:
                  '¿Estás seguro que deseas procesar la orden SIN imprimir?',
              }
            "
          >
            Procesar SIN Imprimir
          </button>

          <button
            class="mt-4 delete-button"
            @click.prevent="
              confirmationModal = {
                action: 'cancelOrder',
                order: order,
                message:
                  '¿Estás seguro que deseas cancelar la orden? Deberás de comunicarlo con el adminstrador para aplicar el reembolso correspondiente.',
              }
            "
          >
            Cancelar Orden
          </button>
        </pending-order>
      </div>

      <div v-else-if="status === 'in_process'">
        <p class="mt-6" v-if="orders.length === 0">No hay órdenes en proceso</p>

        <in-process-order
          v-for="order in orders"
          :key="order.id"
          :order="order"
          class="mt-6 w-full"
          @printMeal="
            confirmationModal = {
              action: 'printMeal',
              order: $event,
              message: '¿Estás seguro que deseas imprimir la etiqueta?',
            }
          "
        >
          <primary-button
            v-if="hasPermission('edit_orders')"
            :loading="loadingOrder"
            @buttonClicked="
              confirmationModal = {
                action: 'completeOrder',
                order: order,
                message: '¿Estás seguro que deseas completar la orden?',
              }
            "
            class="mt-4 mx-0"
            >Completar Orden</primary-button
          >
        </in-process-order>
      </div>

      <div v-else-if="status === 'ready_for_pickup'">
        <p class="mt-6" v-if="orders.length === 0">
          No hay órdenes listas para Pick-Up
        </p>

        <ready-for-pickup-order
          v-for="order in orders"
          :key="order.id"
          :order="order"
          class="mt-6 w-full"
        >
          <primary-button
            v-if="hasPermission('edit_orders')"
            :loading="loadingOrder"
            @buttonClicked="
              confirmationModal = {
                action: 'deliverOrder',
                order: order,
                message: '¿Estás seguro que deseas entregar la orden?',
              }
            "
            class="mt-4 mx-0"
            >Entregar Orden</primary-button
          >
        </ready-for-pickup-order>
      </div>

      <div v-else-if="status === 'ready_for_delivery'">
        <p class="mt-6" v-if="orders.length === 0">
          No hay órdenes listas para entrega
        </p>

        <ready-for-delivery-order
          v-for="order in orders"
          :key="order.id"
          :order="order"
          class="mt-6 w-full"
        >
          <div v-if="order.status === 'ready_for_delivery' && hasPermission('edit_orders')">
            <div v-if="!order.address">
              <p class="mt-4 text-error-500 font-medium">
                ERROR: Esta orden está marcada para entrega pero no tiene dirección asignada.
                Contacta al administrador para corregir el plan.
              </p>
            </div>
            <div v-else-if="isValidForShipping(order)">
              <p v-if="order.shippingPrice == null" class="mt-4">
                Debes asignar un precio de envío ya que se encuentra fuera del
                rango local.
              </p>

              <button
                @click.prevent="orderToSetShippingPrice = order"
                class="secondary-button mt-4 mx-0"
              >
                {{ order.shippingPrice != null ? "Cambiar" : "Agregar" }} precio de envío
              </button>

              <div v-if="order.shippingPrice != null">
                <primary-button
                  :loading="loadingOrder"
                  @buttonClicked="validateSendOrder(order)"
                  class="mt-4 mx-0"
                  >Enviar Orden por
                  {{ integerToCurrency(order.shippingPrice) }}</primary-button
                >
              </div>
            </div>
          </div>
        </ready-for-delivery-order>
      </div>

      <div v-else-if="status === 'delivered'">
        <p class="mt-6" v-if="orders.length === 0">
          No hay órdenes entregadas / enviadas
        </p>

        <delivered-order
          v-for="order in orders"
          :key="order.id"
          :order="order"
          @orderFailed="failedOrder = order"
          @updateTrackingInfo="loadOrders"
          class="mt-6 w-full"
        >
        </delivered-order>
      </div>

      <div v-else-if="status === 'failed_to_send'">
        <p class="mt-6" v-if="orders.length === 0">
          No hay órdenes fallidas al enviar
        </p>

        <failed-to-send-order
          v-for="order in orders"
          :key="order.id"
          :order="order"
          class="mt-6 w-full"
        >
        </failed-to-send-order>
      </div>

      <div v-else-if="!enableStatusFilters">
        <p class="mt-6" v-if="orders.length === 0">
          No hay órdenes encontradas
        </p>

        <general-order
          v-for="order in orders"
          :key="order.id"
          :order="order"
          @orderFailed="failedOrder = order"
          @updateTrackingInfo="loadOrders"
          @printMeal="
            confirmationModal = {
              action: 'printMeal',
              order: $event,
              message: '¿Estás seguro que deseas imprimir la etiqueta?',
            }
          "
          class="mt-6 w-full"
        >
          <primary-button
            v-if="
              order.status === 'pending' &&
              canProcessOrderOrder(order) &&
              hasPermission('edit_orders')
            "
            :loading="loadingOrder"
            @buttonClicked="
              confirmationModal = {
                action: 'processOrder',
                order: order,
                message:
                  '¿Estás seguro que deseas procesar e imprimir la orden?',
              }
            "
            class="mt-4 mx-0"
            >Procesar e Imprimir Orden
          </primary-button>

          <button
            v-if="
              order.status === 'pending' &&
              canProcessOrderOrder(order) &&
              hasPermission('edit_orders')
            "
            class="secondary-button mt-4 mx-0"
            @click.prevent="
              confirmationModal = {
                action: 'processOrderWithoutPrint',
                order: order,
                message:
                  '¿Estás seguro que deseas procesar la orden SIN imprimir?',
              }
            "
          >
            Procesar SIN Imprimir
          </button>

          <primary-button
            v-else-if="
              order.status === 'in_process' && hasPermission('edit_orders')
            "
            :loading="loadingOrder"
            @buttonClicked="
              confirmationModal = {
                action: 'completeOrder',
                order: order,
                message: '¿Estás seguro que deseas completar la orden?',
              }
            "
            class="mt-4 mx-0"
            >Completar Orden</primary-button
          >

          <primary-button
            v-if="
              order.status === 'ready_for_pickup' &&
              hasPermission('edit_orders')
            "
            :loading="loadingOrder"
            @buttonClicked="
              confirmationModal = {
                action: 'deliverOrder',
                order: order,
                message: '¿Estás seguro que deseas entregar la orden?',
              }
            "
            class="mt-4 mx-0"
            >Entregar Orden</primary-button
          >

          <div v-if="order.status === 'ready_for_delivery' && hasPermission('edit_orders')">
            <div v-if="!order.address">
              <p class="mt-4 text-error-500 font-medium">
                ERROR: Esta orden está marcada para entrega pero no tiene dirección asignada.
                Contacta al administrador para corregir el plan.
              </p>
            </div>
            <div v-else-if="isValidForShipping(order)">
              <p v-if="order.shippingPrice == null" class="mt-4">
                Debes asignar un precio de envío ya que se encuentra fuera del
                rango local.
              </p>

              <button
                @click.prevent="orderToSetShippingPrice = order"
                class="secondary-button mt-4 mx-0"
              >
                {{ order.shippingPrice != null ? "Cambiar" : "Agregar" }} precio de envío
              </button>

              <div v-if="order.shippingPrice != null">
                <primary-button
                  :loading="loadingOrder"
                  @buttonClicked="validateSendOrder(order)"
                  class="mt-4 mx-0"
                  >Enviar Orden por
                  {{ integerToCurrency(order.shippingPrice) }}</primary-button
                >
              </div>
            </div>
          </div>
        </general-order>
      </div>

      <pagination
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-change="onPageChange"
      />
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
      </div>
    </div>

    <transition name="modal">
      <confirmation-modal
        v-if="confirmationModal"
        :loading="loadingOrder"
        :message="confirmationModal.message"
        @close="confirmationModal = null"
        @confirm="
          confirmAction(confirmationModal.action, confirmationModal.order)
        "
      />
    </transition>
  </div>
</template>

<script>
import PendingOrder from "@/components/orders/Pending";
import InProcessOrder from "@/components/orders/InProcess";
import ReadyForPickupOrder from "@/components/orders/ReadyForPickup";
import ReadyForDeliveryOrder from "@/components/orders/ReadyForDelivery";
import DeliveredOrder from "@/components/orders/Delivered";
import FailedToSendOrder from "@/components/orders/FailedToSend";
import GeneralOrder from "@/components/orders/General";
import Diet from "@/components/models/Diet";
import MealSet from "@/components/models/MealSet";
import MealSetForm from "@/components/modals/MealSetForm";
import AlertModal from "@/components/ui/AlertModal";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import Pagination from "@/components/ui/Pagination.vue";
import RejectionModal from "@/components/ui/RejectionModal";
import ShippingPriceModal from "@/components/modals/ShippingPriceModal";
import StatusSideNav from "@/components/ui/StatusSideNav";
import TrackingInfoModal from "@/components/modals/TrackingInfoModal";
import api from "@/api";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "Orders",

  components: {
    PendingOrder,
    InProcessOrder,
    ReadyForPickupOrder,
    ReadyForDeliveryOrder,
    DeliveredOrder,
    FailedToSendOrder,
    GeneralOrder,
    Diet,
    MealSet,
    MealSetForm,
    AlertModal,
    ConfirmationModal,
    Pagination,
    RejectionModal,
    ShippingPriceModal,
    StatusSideNav,
    TrackingInfoModal,
  },

  data() {
    return {
      dietCategories: null,
      dietStages: null,
      dietTypes: null,
      inventories: null,
      orders: null,
      currentPage: 1,
      totalPages: 0,
      loading: false,
      loadingOrder: false,
      mealSetToUpdate: null,
      alertModalMessage: null,
      confirmationModal: null,
      failedOrder: false,
      refundShipping: false,
      searchText: null,
      status: null,
      enableStatusFilters: true,
      petTypeFilter: null,
      orderType: null,
      shippingPrice: null,
      orderToSetShippingPrice: null,
      orderToSendWithTracking: null,
      carriers: null,
    };
  },

  created() {
    this.$store.commit("setSelectedTab", "orders");

    this.status = this.$route.query.status;
    this.searchText = this.$route.query.search;
    this.petTypeFilter = this.$route.query.petType;
    this.orderType = this.$route.query.orderType;
    this.enableStatusFilters = this.status != null && this.status != "";

    this.loadDietCategories();
    this.loadDietStages();
    this.loadDietTypes();
    this.loadOrders();
    this.loadCarriers();
  },

  computed: {
    statusNavOptions() {
      const query = (status) => ({
        status,
        search: this.searchText,
        petType: this.petTypeFilter,
        orderType: this.orderType,
      });

      return [
        { status: "pending", label: "Pendientes", icon: "🕐", activeClass: "text-pending" },
        { status: "in_process", label: "En Proceso", icon: "⚙️", activeClass: "text-inProcess" },
        { status: "ready_for_pickup", label: "Listas para Pick-Up", icon: "🛍️", activeClass: "text-ready" },
        { status: "ready_for_delivery", label: "Listas para Enviar", icon: "📦", activeClass: "text-ready" },
        { status: "delivered", label: "Entregadas / Enviadas", icon: "✅", activeClass: "text-delivered" },
        { status: "failed_to_send", label: "Fallidas al Enviar", icon: "⚠️", activeClass: "text-failed" },
      ].map((option) => ({
        label: option.label,
        icon: option.icon,
        to: { name: "ordersIndex", query: query(option.status) },
        active: this.status === option.status,
        activeClass: option.activeClass,
      }));
    },
  },

  methods: {
    integerToCurrency,

    async loadDietCategories() {
      this.loading = true;

      try {
        this.dietCategories = await api.resources.getDietCategories();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: this.previousRoute });
      }

      this.loading = false;
    },

    async loadDietStages() {
      this.loading = true;

      try {
        this.dietStages = await api.resources.getDietStages();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: this.previousRoute });
      }

      this.loading = false;
    },

    async loadDietTypes() {
      this.loading = true;

      try {
        this.dietTypes = await api.resources.getDietTypes();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: this.previousRoute });
      }

      this.loading = false;
    },

    async loadInventories() {
      this.loading = true;

      try {
        this.inventories = await api.inventories.getByBranch(
          this.$route.params.branchId
        );
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    async loadOrders() {
      if (!this.inventories) {
        this.loadInventories();
      }

      this.loading = true;

      try {
        const response = await api.orders.getByBranch(
          this.$route.params.branchId,
          [
            {
              name: "status",
              value: this.status,
            },
            {
              name: "page",
              value: this.currentPage,
            },
            {
              name: "search",
              value: this.searchText,
            },
            {
              name: "petType",
              value: this.petTypeFilter,
            },
            {
              name: "orderType",
              value: this.orderType,
            },
          ]
        );
        this.orders = response.data;
        this.currentPage = response.current_page;
        this.totalPages = response.last_page;
        this.calculateMealSetSummary();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    async loadCarriers() {
      try {
        this.carriers = await api.resources.getCarriers();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
    },

    search() {
      this.currentPage = 1;
      this.$router.push({
        name: "ordersIndex",
        query: {
          status: this.status,
          search: this.searchText,
          petType: this.petTypeFilter,
          orderType: this.orderType,
        },
      });
      this.loadOrders();
    },

    changeStatusFilter() {
      this.enableStatusFilters = !this.enableStatusFilters;

      this.$router.push({
        name: "ordersIndex",
        query: {
          status: this.enableStatusFilters ? "pending" : "",
          search: this.searchText,
          petType: this.petTypeFilter,
          orderType: this.orderType,
        },
      });
    },

    changePetTypeFilter(petType) {
      if (petType === "dog") {
        if (this.petTypeFilter === "dog") {
          this.petTypeFilter = null;
        } else {
          this.petTypeFilter = "dog";
        }
      } else {
        if (this.petTypeFilter === "cat") {
          this.petTypeFilter = null;
        } else {
          this.petTypeFilter = "cat";
        }
      }

      this.$router.push({
        name: "ordersIndex",
        query: {
          status: this.status,
          search: this.searchText,
          petType: this.petTypeFilter,
          orderType: this.orderType,
        },
      });
    },

    changeOrderTypeFilter() {
      if (this.orderType === "asc") {
        this.orderType = "desc";
      } else if (this.orderType === "desc") {
        this.orderType = "asc";
      } else {
        this.orderType = "desc";
      }

      this.$router.push({
        name: "ordersIndex",
        query: {
          status: this.status,
          search: this.searchText,
          petType: this.petTypeFilter,
          orderType: this.orderType,
        },
      });
    },

    closeMealSetForm() {
      this.mealSetToUpdate = null;
    },

    mealSetUpdated() {
      this.mealSetToUpdate = null;
      this.loadOrders();
    },

    calculateMealSetSummary() {
      if (!this.orders) {
        return;
      }

      this.orders.forEach((order) => {
        if (order.status === "ready_for_delivery" && order.address && order.address.branches) {
          const branch = order.address.branches.filter(
            (br) => br.id === order.branch_id
          )[0];

          if (branch) {
            order.shippingPrice = branch.pivot.shipping_price;
          }
        }

        let categories = {};

        order.diet.collapsed = true;

        order.meal_sets.forEach((mealSet) => {
          mealSet.collapsed = true;

          mealSet.meals.forEach((meal) => {
            meal.portions.forEach((portion) => {
              const cat = portion.diet_subcategory.diet_category;
              if (!categories[cat.id]) {
                categories[cat.id] = {
                  id: cat.id,
                  name: cat.name,
                  subcategories: {},
                };
              }
              const subcat = portion.diet_subcategory;
              if (!categories[cat.id].subcategories[subcat.id]) {
                categories[cat.id].subcategories[subcat.id] = {
                  id: subcat.id,
                  name: subcat.name,
                  totalQuantity: 0,
                  available: false,
                };
              }
              categories[cat.id].subcategories[subcat.id].totalQuantity +=
                portion.quantity;
            });
          });
        });

        let summary = Object.values(categories).map((category) => ({
          ...category,
          subcategories: Object.values(category.subcategories),
        }));

        summary.forEach((cat) => {
          cat.subcategories.forEach((subcat) => {
            subcat.available = this.hasAvailableInventory(
              subcat.id,
              subcat.totalQuantity
            );
          });
        });

        order.summary = summary;
      });
    },

    hasAvailableInventory(subcategoryId, quantity) {
      const inventory = this.inventories.filter(
        (inv) => inv.diet_subcategory_id === subcategoryId
      )[0];

      if (!inventory) {
        return false;
      }

      return inventory.quantity >= quantity;
    },

    canProcessOrderOrder(order) {
      if (!order.summary) {
        return false;
      }

      for (let cat of order.summary) {
        for (let subcat of cat.subcategories) {
          if (!subcat.available) {
            return false;
          }
        }
      }

      return true;
    },

    isValidForShipping(order) {
      if (!order.address || !order.address.branches) {
        return false;
      }

      const branch = order.address.branches.filter(
        (br) => br.id === order.branch_id
      )[0];

      return branch ? branch.pivot.valid_for_shipping : false;
    },

    confirmAction(action, order) {
      this[action](order);
      this.confirmationModal = null;
    },

    async processOrder(order) {
      this.loadingOrder = true;

      try {
        const response = await api.orders.processOrder(order.id, {
          summary: order.summary,
        });
        this.$store.dispatch("successSnack", response.message);
        await this.loadInventories();
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingOrder = false;
    },

    async processOrderWithoutPrint(order) {
      this.loadingOrder = true;

      try {
        const response = await api.orders.processOrder(order.id, {
          summary: order.summary,
          skip_print: true,
        });
        this.$store.dispatch("successSnack", response.message);
        await this.loadInventories();
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingOrder = false;
    },

    async printMeal(mealId) {
      this.loadingOrder = true;

      try {
        const response = await api.orders.printMeal(mealId);
        this.$store.dispatch("successSnack", response.message);
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingOrder = false;
    },

    async completeOrder(order) {
      this.loadingOrder = true;

      try {
        const response = await api.orders.completeOrder(order.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingOrder = false;
    },

    validateSendOrder(order) {
      if (
        !order.related_shipment_orders.every(
          (or) => or.status === "ready_for_delivery"
        )
      ) {
        this.alertModalMessage = `Esta orden debe enviarse con más órdenes, por lo tanto todas deben de estar en 'Listas para Enviar'.`;
        return;
      }

      // Show tracking info modal instead of confirmation
      this.orderToSendWithTracking = order;
    },

    async sendOrderWithTrackingInfo() {
      // This is called when TrackingInfoModal saves the tracking info
      // The modal already updated the order's carrier and tracking_number
      await this.sendOrder(this.orderToSendWithTracking);
      this.orderToSendWithTracking = null;
    },

    async sendOrder(order) {
      this.loadingOrder = true;

      const request = {
        shipping_price: order.shippingPrice,
        carrier: order.carrier,
        tracking_number: order.tracking_number,
      };

      try {
        const response = await api.orders.sendOrder(order.id, request);
        this.$store.dispatch("successSnack", response.message);
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingOrder = false;
    },

    async deliverOrder(order) {
      this.loadingOrder = true;

      try {
        const response = await api.orders.deliverOrder(order.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingOrder = false;
    },

    async failedToSend(reason) {
      this.loadingOrder = true;

      try {
        const response = await api.orders.failedToSend(this.failedOrder.id, {
          fail_reason: reason,
          refund_shipping: this.refundShipping,
        });
        this.$store.dispatch("successSnack", response.message);
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.failedOrder = null;
      this.refundShipping = false;
      this.loadingOrder = false;
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadOrders();
    },

    async cancelOrder(order) {
      this.loadingOrder = true;

      try {
        const response = await api.orders.cancelOrder(order.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingOrder = false;
    },
  },

  watch: {
    "$route.query": {
      immediate: true,
      handler(newQuery, oldQuery) {
        this.status = newQuery.status;
        this.petTypeFilter = newQuery.petType;
        this.orderType = newQuery.orderType;

        this.loadOrders();
      },
    },
  },
};
</script>